import React, { Suspense } from 'react';
import { Refine } from '@refinedev/core';
import { DevtoolsProvider } from '@refinedev/devtools';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import { useNotificationProvider } from '@refinedev/antd';
import routerBindings from '@refinedev/react-router-v6';

import { ColorModeContextProvider } from '@contexts/color-mode';
import { authProvider } from '@providers/auth-provider';
import { dataProvider } from '@providers/data-provider';
import { LayoutWrapper } from '@components/layout';
import { AppRoutes } from './routes';
import { LoadingScreen } from '@components/LoadingScreen';
import {
  DashboardOutlined,
  UserOutlined,
  BookOutlined,
  CalendarOutlined,
  FileTextOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  CreditCardOutlined,
  MailOutlined,
} from "@ant-design/icons";
import '@refinedev/antd/dist/reset.css';

// Resources for tutor app
const tutorResources = [
  {
    name: "dashboard",
    list: "/dashboard",
    meta: {
      label: "Dashboard",
      icon: <DashboardOutlined />,
    },
  },
  {
    name: "operations",
    list: "/operations",
    meta: {
      label: "Operations",
      icon: <HomeOutlined />,
    },
  },
  {
    name: "students",
    list: "/students",
            create: "/students",
        edit: "/students",
    show: "/students/show/:id",
    meta: {
      label: "Students",
      icon: <UserOutlined />,
    },
  },
  {
    name: "families",
    meta: {
      label: "Families (Internal)",
    },
  },
  {
    name: "lessons",
    list: "/lessons",
    create: "/lessons/create",
    edit: "/lessons/edit/:id",
    show: "/lessons/show/:id",
    meta: {
      label: "Lessons",
      icon: <BookOutlined />,
    },
  },
  {
    name: "calendar",
    list: "/calendar",
    meta: {
      label: "Calendar",
      icon: <CalendarOutlined />,
    },
  },
  {
    name: "invoices",
    list: "/invoices",
    create: "/invoices/create",
    edit: "/invoices/edit/:id",
    show: "/invoices/show/:id",
    meta: {
      label: "Invoices",
      icon: <FileTextOutlined />,
    },
  },
  {
    name: "locations",
    list: "/locations",
    create: "/locations/create",
    edit: "/locations/edit/:id",
    show: "/locations/show/:id",
    meta: {
      label: "Locations",
      icon: <EnvironmentOutlined />,
    },
  },
  {
    name: "email-templates",
    list: "/email-templates",
    meta: {
      label: "Email Templates",
      icon: <MailOutlined />,
    },
  },
  {
    name: "profile",
    list: "/profile",
    meta: {
      label: "Profile",
      icon: <UserOutlined />,
    },
  },
  {
    name: "subscription",
    list: "/subscription",
    meta: {
      label: "Subscription",
      icon: <CreditCardOutlined />,
    },
  },
];

function App() {
  // Only show DevTools in development and when explicitly enabled
  const shouldShowDevTools = import.meta.env.DEV && import.meta.env.VITE_SHOW_DEVTOOLS === "true";

  const AppContent = () => (
    <LayoutWrapper>
      <Suspense fallback={<LoadingScreen />}>
        <AppRoutes />
      </Suspense>
      <RefineKbar />
    </LayoutWrapper>
  );

  return (
    <RefineKbarProvider>
      <ColorModeContextProvider>
        <Refine
          routerProvider={routerBindings}
          dataProvider={dataProvider}
          notificationProvider={useNotificationProvider}
          authProvider={authProvider}
          resources={tutorResources}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
            useNewQueryKeys: true,
            projectId: "stagenote-tutor-app",
          }}
        >
          {shouldShowDevTools ? (
            <DevtoolsProvider>
              <AppContent />
            </DevtoolsProvider>
          ) : (
            <AppContent />
          )}
        </Refine>
      </ColorModeContextProvider>
    </RefineKbarProvider>
  );
}

export default App; 